import { useState } from "react";
import { FilePdf } from "@phosphor-icons/react";
import { useApi, monthLabel } from "@/lib/api";
import { Section, Btn, Modal, useLoad } from "@/components/app/common";
import { downloadFile } from "@/pages/Inspections";
import { ChecklistMonth } from "@/pages/Calendar";

const tone = (pct, live) => (pct == null ? "bg-slate-50 text-slate-400" : live ? "bg-slate-100 text-slate-700 ring-1 ring-inset ring-slate-300" : pct === 100 ? "bg-green-50 text-green-800" : pct >= 80 ? "bg-amber-50 text-amber-800" : "bg-red-50 text-red-700");
const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const short = (m) => MON[+m.slice(5) - 1];

export function ChecklistScores({ groupId }) {
  const api = useApi();
  const g = groupId || "all";
  const [s] = useLoad(() => api.get(`/calendar/scores?group_id=${g}`), [g]);
  const [open, setOpen] = useState(null);
  if (!s || !s.farms.length || !s.farms.some((r) => r.total)) return null;
  return (
    <div className="mt-6"><Section title={`Checklist scores · season ${s.season}`} testid="checklist-scores"
      actions={<Btn variant="outline" onClick={() => downloadFile(api, `/calendar/scores/pdf?group_id=${g}`, `checklist-scores-${s.season}.pdf`)} data-testid="scores-pdf"><FilePdf size={16} />PDF</Btn>}>
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="scores-table">
          <thead><tr className="text-left border-b border-slate-200">
            <th className="py-2 pr-3 label-sm sticky left-0 bg-white z-10">Farm</th>
            {s.months.map((m) => <th key={m} className="py-2 px-1 label-sm text-center">{short(m)}</th>)}
            <th className="py-2 pl-3 label-sm text-center">Season</th>
          </tr></thead>
          <tbody>{s.farms.map((r) => (
            <tr key={r.farm_id} className="border-b border-slate-100" data-testid={`scores-row-${r.farm_id}`}>
              <td className="py-2 pr-3 font-medium whitespace-nowrap sticky left-0 bg-white z-10">{r.farm_name}</td>
              {r.cells.map((c) => (
                <td key={c.month} className="py-1.5 px-1">
                  <button disabled={c.pct == null} onClick={() => setOpen({ farm: { id: r.farm_id, name: r.farm_name }, month: c.month })} data-testid={`score-${r.farm_id}-${c.month}`}
                    className={`w-full min-w-[64px] rounded px-1.5 py-1 text-xs font-semibold font-mono-num transition-[filter] hover:brightness-95 disabled:cursor-default ${tone(c.pct, c.in_progress)}`}
                    title={c.pct == null ? "No items" : `${c.done} of ${c.total} ticked${c.in_progress ? " — month in progress" : ""}`}>
                    {c.pct == null ? "—" : <>{c.done}/{c.total}<span className="block font-normal">{c.pct}%</span></>}
                  </button>
                </td>))}
              <td className="py-1.5 pl-3"><span className={`block text-center rounded px-2 py-1 text-xs font-bold ${tone(r.pct)}`} data-testid={`score-${r.farm_id}-season`}>{r.pct == null ? "—" : `${r.pct}%`}</span></td>
            </tr>))}</tbody>
        </table>
      </div>
      <p className="text-xs text-slate-500 mt-3">Green 100% · amber 80–99% · red under 80% · grey outline = this month, still in progress (not counted in the season %). Click a month to see what was ticked.</p>
      <Modal open={!!open} onClose={() => setOpen(null)} title={open ? `${open.farm.name} — ${monthLabel(open.month)}` : ""} wide>
        {open && <ChecklistMonth farm={open.farm} month={open.month} />}
      </Modal>
    </Section></div>
  );
}
